import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { PendingStatus } from "@/components/ui/pending-indicator";
import {
  fetchDeskTopProductsRange,
  type DeskGlanceTopProduct,
} from "@/shared/api/desk";
import { formatGlanceMoney } from "./owner-glance-math";
import { HomeMetricCard } from "./HomeMetricCard";
import {
  HomePeriodCustomForm,
  HomePeriodPresetToggle,
} from "./HomePeriodControls";
import {
  homePeriodEmptyMessage,
  homePeriodLabel,
  loadHomePeriod,
  saveHomePeriod,
  type HomePeriodState,
} from "./home-metric-period";

const COMPACT_N = 3;

function RankList(props: {
  rows: DeskGlanceTopProduct[];
  companySlug: string;
  currencyCode: string;
  limit: number;
}) {
  return (
    <ol className="m-0 list-none space-y-2 p-0">
      {props.rows.slice(0, props.limit).map((row, i) => {
        const money =
          formatGlanceMoney(row.amount, props.currencyCode, {
            maxFractionDigits: 0,
            zeroOk: true,
          }) ?? row.amount;
        return (
          <li key={row.productId}>
            <Link
              to={`/c/${props.companySlug}/products/${row.productId}`}
              className="grid grid-cols-[1.25rem_1fr_auto] items-baseline gap-2 no-underline"
            >
              <span className="text-xs tabular-nums text-[var(--color-muted)]">
                {i + 1}.
              </span>
              <span className="min-w-0 truncate text-sm font-medium text-[var(--color-foreground)]">
                {row.name}
              </span>
              <span className="text-xs tabular-nums text-[var(--color-muted)]">
                {money}
              </span>
            </Link>
          </li>
        );
      })}
    </ol>
  );
}

function todayYmdLocal(): string {
  const n = new Date();
  const y = n.getFullYear();
  const m = String(n.getMonth() + 1).padStart(2, "0");
  const d = String(n.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function monthStartYmd(ymd: string): string {
  return `${ymd.slice(0, 7)}-01`;
}

/** Compact Top products → Show more — plan/50 period presets + custom. */
export function TopProductsWidget(props: {
  companySlug: string;
  currencyCode: string;
  rows7: DeskGlanceTopProduct[] | null | undefined;
  rows30: DeskGlanceTopProduct[] | null | undefined;
  rowsMtd: DeskGlanceTopProduct[] | null | undefined;
  loading?: boolean;
  onCustomize?: () => void;
}) {
  const [period, setPeriod] = useState<HomePeriodState>(() =>
    loadHomePeriod("topProducts", props.companySlug),
  );
  const [showMoreOpen, setShowMoreOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState(
    () => period.from ?? monthStartYmd(todayYmdLocal()),
  );
  const [draftTo, setDraftTo] = useState(() => period.to ?? todayYmdLocal());
  const [customError, setCustomError] = useState<string | null>(null);

  const customQuery = useQuery({
    queryKey: [
      "desk",
      "top-products-range",
      props.companySlug,
      period.from,
      period.to,
    ],
    queryFn: ({ signal }) =>
      fetchDeskTopProductsRange(
        props.companySlug,
        period.from!,
        period.to!,
        signal,
      ),
    enabled:
      period.preset === "custom" && Boolean(period.from && period.to),
  });

  const presetRows =
    period.preset === "30"
      ? props.rows30
      : period.preset === "mtd"
        ? props.rowsMtd
        : period.preset === "custom"
          ? customQuery.data?.rows
          : props.rows7;
  const rows = presetRows ?? [];
  const loadingPreset =
    props.loading &&
    period.preset !== "custom" &&
    presetRows == null;
  const loadingCustom =
    period.preset === "custom" && customQuery.isPending && !customQuery.data;
  const description = homePeriodLabel(period, "products");

  const persist = (next: HomePeriodState) => {
    setPeriod(next);
    saveHomePeriod("topProducts", props.companySlug, next);
  };

  const onPreset = (preset: "7" | "30" | "mtd") => {
    setCustomError(null);
    persist({ preset });
  };

  const onCustomClick = () => {
    setDraftFrom(period.from ?? monthStartYmd(todayYmdLocal()));
    setDraftTo(period.to ?? todayYmdLocal());
    setShowMoreOpen(true);
  };

  const onApplyCustom = () => {
    setCustomError(null);
    persist({ preset: "custom", from: draftFrom, to: draftTo });
  };

  const bodyLoading = loadingPreset || loadingCustom;
  const list = (
    <RankList
      rows={rows}
      companySlug={props.companySlug}
      currencyCode={props.currencyCode}
      limit={COMPACT_N}
    />
  );
  const listFull = (
    <RankList
      rows={rows}
      companySlug={props.companySlug}
      currencyCode={props.currencyCode}
      limit={8}
    />
  );

  return (
    <HomeMetricCard
      title="Top products"
      description={description}
      expandedDescription="Recognized sales (POS + delivered SO − returns). Tap a row for the product."
      onCustomize={props.onCustomize}
      actions={
        <HomePeriodPresetToggle
          state={period}
          onPreset={onPreset}
          onCustom={onCustomClick}
        />
      }
      showMoreOpen={showMoreOpen}
      onShowMoreOpenChange={setShowMoreOpen}
      expandedExtra={
        <HomePeriodCustomForm
          idPrefix="top-products-period"
          from={draftFrom}
          to={draftTo}
          onFrom={setDraftFrom}
          onTo={setDraftTo}
          onApply={onApplyCustom}
          pending={customQuery.isFetching && period.preset === "custom"}
          error={
            customError ??
            (customQuery.isError ? "Could not load this range." : null)
          }
        />
      }
      empty={!bodyLoading && rows.length === 0}
      emptyMessage={homePeriodEmptyMessage(period, "products")}
      compact={
        bodyLoading ? <PendingStatus>Loading…</PendingStatus> : list
      }
      expanded={
        bodyLoading ? <PendingStatus>Loading…</PendingStatus> : listFull
      }
    />
  );
}
